import type { FlagValues, NavigationEntry, ScreenDeclaration } from "./web-module.ts";

// ADR-006 §7: nothing gated is drawn before the active organization's flags arrive, and
// only what resolves on afterwards. Pure, so the shell's filter and a screen's gate are
// unit tested without rendering anything (`createUi` is the only caller at runtime).

/** A screen or a navigation entry with no flag is always allowed. One still loading its
 * flag, or whose flag resolved off, is not: the two differ only in whether anything draws
 * at all (nothing, while loading) or the not-found screen (resolved off). */
export function flagAllows(flag: string | undefined, values: FlagValues | undefined): boolean {
  if (flag === undefined) return true;
  return values?.[flag] === true;
}

/** True while a flagged screen or entry's value has not arrived yet: draw nothing, not even
 * the not-found screen, so a slow read never flashes either state. */
export function flagPending(flag: string | undefined, values: FlagValues | undefined): boolean {
  return flag !== undefined && values === undefined;
}

/** The shell's own filter (ADR-006 §7 consequence): every entry whose flag is on, or has
 * none; nothing gated before `values` arrives. */
export function visibleNavigation<TEntry extends NavigationEntry>(
  entries: readonly TEntry[],
  values: FlagValues | undefined,
): readonly TEntry[] {
  return entries.filter((entry) => flagAllows(entry.flag, values));
}

/** Whether the matched screen may render as itself. `flagPending` must be checked first:
 * while pending this answers false too, but the caller draws nothing rather than the
 * not-found screen. */
export function screenAllowed(screen: ScreenDeclaration, values: FlagValues | undefined): boolean {
  return flagAllows(screen.flag, values);
}
